/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import MainBody from "../../components/MainBody/MainBody";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  fileUpload,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { useOrganization } from "../../lib/context/OrganisationContext";
import { useTrust } from "../../lib/context/TrustContext";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import BootstrapTable from "react-bootstrap-table-next";
import UnauthorisedAccess from "../../UnauthorisedAccess";
import AddEditInvoicing from "./AddEditInvoicing";
import {
  formatNumber,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "../../lib/Misc";
import { InvoiceModuleConditon } from "../../lib/PermissionConditions";

import moment from "moment";
import Confirmation from "../../components/Confirmation";
import InvoicingActionButton from "./InvoicingActionButton";
import ExpandInvoicing from "./ExpandInvoicing";
import {
  CaretDownIcon,
  CaretRightIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import Pagination from "../../components/Pagination";
import { AnimatedTooltip } from "../../components/AnimatedTooltip";
import UploadInvoice from "./UploadInvoice";
import { cn } from "../../lib/utils";

const Invoicing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationData } = useOrganization();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [invoicingList, setInvoicingsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId, trustData } = useTrust();

  const [invoicingId, setInvoicingId] = useState(null);
  const [showAddEditInvoicing, setShowAddEditInvoicing] = useState(false);
  const [showInvoicingDelete, setShowInvoicingDelete] = useState(false);
  const [showInvoicingApprove, setShowInvoicingApprove] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const toggleShowAddEditInvoicing = () => {
    if (showAddEditInvoicing) {
      setEdit(false);
      setEditItem(null);
      setInvoicingId(null);
    }
    setShowAddEditInvoicing(!showAddEditInvoicing);
  };

  const toggleShowDelete = () => {
    if (showInvoicingDelete) {
      setInvoicingId(null);
    }
    setShowInvoicingDelete(!showInvoicingDelete);
  };

  const toggleShowApprove = () => {
    if (showInvoicingApprove) {
      setInvoicingId(null);
    }
    setShowInvoicingApprove(!showInvoicingApprove);
  };

  const fetchInvoicings = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_INVOICINGS}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const invoicingList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < invoicingList?.length; i++) {
            notSelectedIds.push(invoicingList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setInvoicingsList(invoicingList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setInvoicingsList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId]
  );

  const invoicingsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: invoicingList[i]?.id,
        date: invoicingList[i]?.date
          ? moment(invoicingList[i]?.date, "YYYY-MM-DD").format("DD-MM-YYYY")
          : "",
        due_date: invoicingList[i]?.due_date
          ? moment(invoicingList[i]?.due_date, "YYYY-MM-DD").format(
              "DD-MM-YYYY"
            )
          : "",
        invoice_number: invoicingList[i]?.invoice_number
          ? invoicingList[i]?.invoice_number
          : "",

        contact: invoicingList[i]?.contact
          ? formatSubcontractorName(invoicingList[i]?.contact)
          : "",

        note: invoicingList[i]?.note ? invoicingList[i]?.note : "",

        total_amount_inc: invoicingList[i]?.subtotal_amount
          ? `$${formatNumber(Number(invoicingList[i]?.subtotal_amount))}`
          : "$0.00",

        total_processing_fee_amount: invoicingList[i]
          ?.total_processing_fee_amount
          ? `$${formatNumber(
              Number(invoicingList[i]?.total_processing_fee_amount)
            )}`
          : "$0.00",

        total_premium_amount: invoicingList[i]?.total_premium_amount
          ? `$${formatNumber(Number(invoicingList[i]?.total_premium_amount))}`
          : "$0.00",

        total_discount_amount: invoicingList[i]?.total_discount_amount
          ? `$${formatNumber(Number(invoicingList[i]?.total_discount_amount))}`
          : "$0.00",

        total_amount: invoicingList[i]?.total_amount
          ? `$${formatNumber(Number(invoicingList[i]?.total_amount))}`
          : "$0.00",

        subtotal_amount: invoicingList[i]?.total_premium_amount
          ? `$${formatNumber(
              Number(invoicingList[i]?.subtotal_amount) +
                Number(invoicingList[i]?.total_premium_amount) +
                Number(invoicingList[i]?.total_processing_fee_amount)
            )}`
          : invoicingList[i]?.subtotal_amount
          ? `$${formatNumber(Number(invoicingList[i]?.subtotal_amount))}`
          : "$0.00",

        total_gst_amount: invoicingList[i]?.total_gst_amount
          ? `$${formatNumber(Number(invoicingList[i]?.total_gst_amount))}`
          : "$0.00",

        total_net_amount: invoicingList[i]?.total_net_amount ? (
          <div className="flex items-center gap-1">
            {`$${formatNumber(Number(invoicingList[i]?.total_net_amount))}`}{" "}
            <AnimatedTooltip
              bottomAlign={i <= quantity / 2 - 1 ? true : false}
              title={
                <div className="flex flex-col gap-1">
                  {/* <p>
                    <strong>Amount: </strong>
                    {invoicingList[i]?.subtotal_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.subtotal_amount)
                        )}`
                      : "$0.00"}
                  </p>
                  <p>
                    <strong>Fee: </strong>
                    {invoicingList[i]?.total_processing_fee_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.total_processing_fee_amount)
                        )}`
                      : "$0.00"}
                  </p>
                  <p>
                    <strong>Buyer's Premium: </strong>
                    {invoicingList[i]?.total_premium_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.total_premium_amount)
                        )}`
                      : "$0.00"}
                  </p>
                  <p>
                    <strong>Sub Total: </strong>
                    {invoicingList[i]?.total_premium_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.subtotal_amount) +
                            Number(invoicingList[i]?.total_premium_amount) +
                            Number(
                              invoicingList[i]?.total_processing_fee_amount
                            )
                        )}`
                      : invoicingList[i]?.subtotal_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.subtotal_amount)
                        )}`
                      : "$0.00"}
                  </p>
                  <p>
                    <strong>Discount: </strong>
                    {invoicingList[i]?.total_discount_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.total_discount_amount)
                        )}`
                      : "$0.00"}
                  </p> */}

                  <p>
                    <strong>Amount (Ex): </strong>
                    {invoicingList[i]?.total_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.total_amount)
                        )}`
                      : "$0.00"}
                  </p>
                  <p>
                    <strong>Tax: </strong>
                    {invoicingList[i]?.total_gst_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.total_gst_amount)
                        )}`
                      : "$0.00"}
                  </p>

                  {/* <p>
                    <strong>Net Amount: </strong>
                    {invoicingList[i]?.total_net_amount
                      ? `$${formatNumber(
                          Number(invoicingList[i]?.total_net_amount)
                        )}`
                      : "$0.00"}
                  </p> */}
                </div>
              }
              // className={i <= quantity / 2 - 1 ? "top-6" : "-top-44"}
              className={i <= quantity / 2 - 1 ? "top-6" : "-top-14"}
            >
              <InfoCircledIcon className="text-webPrimary" />
            </AnimatedTooltip>
          </div>
        ) : (
          "$0.00"
        ),

        status:
          invoicingList[i]?.status == 2
            ? "Rejected"
            : invoicingList[i]?.status == 1
            ? "Accepted"
            : "Pending",
        invoice_items: invoicingList[i]?.invoice_items
          ? invoicingList[i]?.invoice_items
          : [],
        action: (
          <>
            <InvoicingActionButton
              id={invoicingList[i]?.id}
              setInvoicingId={setInvoicingId}
              toggleShowDelete={toggleShowDelete}
              toggleShowApprove={toggleShowApprove}
              used={invoicingList[i]?.is_used ? true : false}
              reconciled={invoicingList[i]?.is_reconciled ? true : false}
              status={invoicingList[i]?.status}
              setEdit={setEdit}
              setEditItem={setEditItem}
              item={invoicingList[i]}
              setLoading={setLoading}
              trustId={trustId}
              toggleShowAddEditInvoicing={toggleShowAddEditInvoicing}
            />
          </>
        ),
      });
    }
    return items;
  };

  const invoicings = invoicingsGenerator(invoicingList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "date",
      text: "Date",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "due_date",
      text: "Due Date",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "invoice_number",
      text: "Invoice No.",
    },
    {
      headerClasses: cn(
        THClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      classes: cn(
        TDClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      dataField: "contact",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "contacts",
        "Customer"
      )}`,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "note",
      text: "Note",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_amount_inc",
      text: "Amount",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_processing_fee_amount",
      text: "Fees",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_premium_amount",
      text: "Buyer's Premium",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "subtotal_amount",
      text: "Sub Total",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_discount_amount",
      text: "Discount",
    },
    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "total_amount",
    //   text: "Amount(Exc)",
    // },
    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "total_gst_amount",
    //   text: "Tax",
    // },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_net_amount",
      text: "Net Amount",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "status",
      text: "Status",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchInvoicings();
  }, [fetchInvoicings]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchInvoicings(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchInvoicings(limit, page);
  };

  // Single COA Delete
  const handleInvoicingDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.INVOICING_DELETE}/${invoicingId}`);
      setLoading(false);
      fetchInvoicings(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  // Single COA Delete
  const handleInvoicingApprove = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("status", 1);
      await fileUpload(`${API.INVOICING_STATUS}/${invoicingId}`, formData);
      setLoading(false);
      fetchInvoicings(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  const expandRow = {
    renderer: (row) => (
      <>
        <ExpandInvoicing expandData={row?.invoice_items} />
      </>
    ),
    onlyOneExpanding: false,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };

  return InvoiceModuleConditon(organizationData) ? (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        mainTitle={"Invoices"}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            <UploadInvoice
              fetchData={() => fetchInvoicings(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setLoading}
            />
            {/* Add Invoicing */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Invoice" : "Add Invoice"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setInvoicingId(null);
                // setShowAddEditInvoicing(false);
                setEditItem(null);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Invoice</span>
                </div>
              }
              open={showAddEditInvoicing}
              buttonClassName={""}
              setOpen={setShowAddEditInvoicing}
              className={"md:max-w-[95vw] md:max-h-[95vh]"}
              mobileCancelHidden={true}
              write={true}
            >
              <AddEditInvoicing
                edit={edit}
                editItem={editItem}
                toggleShowAddEditInvoicing={toggleShowAddEditInvoicing}
                id={invoicingId}
                trustId={trustId}
                trustData={trustData}
                fetchInvoicingData={() => fetchInvoicings(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={invoicings}
              columns={columns}
              expandRow={expandRow}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Invoice"}
        open={showInvoicingDelete}
        setOpen={setShowInvoicingDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete invoice?"}
        okAction={async () => {
          handleInvoicingDelete();
          toggleShowDelete();
          // fetchInvoicings(limit, page);
        }}
        cancelAction={() => {
          setInvoicingId(null);
          setShowInvoicingDelete(!showInvoicingDelete);
        }}
      />

      <Confirmation
        title={"Approve Invoice"}
        open={showInvoicingApprove}
        setOpen={setShowInvoicingApprove}
        toggleShow={() => {
          toggleShowApprove();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to approve invoice?"}
        okAction={async () => {
          handleInvoicingApprove();
          toggleShowApprove();
          // fetchInvoicings(limit, page);
        }}
        cancelAction={() => {
          setInvoicingId(null);
          setShowInvoicingApprove(!showInvoicingApprove);
        }}
      />
    </>
  ) : (
    <UnauthorisedAccess />
  );
};

export default Invoicing;
